import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { salesLeaderboard, fmtMoney, fmtMoneyShort } from "../lib/appUtils";
import ProgressRing from "./ProgressRing";
import TargetDialog from "./TargetDialog";
import Avatar from "./Avatar";
import LeaderboardFilters, { LB_DEFAULT, LB_TEAM_REGION } from "./LeaderboardFilters";
import { Trophy, Target, AlertTriangle, BellRing } from "lucide-react";

const RANK_TINT = ["#4F6BFF", "#f59e0b", "#0ea5e9"];

export default function SalesLeaderboard({ projects: scopedProjects }) {
  const { lang, projects, salesTargets, user, api, avatarFor } = useApp();
  const [f, setF] = useState(LB_DEFAULT);
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(false);
  const canEdit = user.role === "admin" || user.role === "ceo";
  const isBehind = (r, tg) => (tg.monthSign > 0 && r.monthSigned < tg.monthSign) || (tg.monthCollect > 0 && r.monthCollected < tg.monthCollect);
  const hasTarget = (tg) => tg.monthSign > 0 || tg.monthCollect > 0 || tg.yearSign > 0 || tg.yearCollect > 0;
  const base = (scopedProjects || projects).filter((p) => f.branch === "all" || LB_TEAM_REGION[p.team] === f.branch);
  // 所有在职销售账号都上榜（/sales-targets 只返回在职、未停用的销售）：无项目的销售显示 0 业绩；已删除 / 停用账号名下的历史项目不再上榜
  const stats = Object.fromEntries(salesLeaderboard(base).map((r) => [r.name, r]));
  const allRows = Object.keys(salesTargets || {})
    .filter((name) => f.branch === "all" || !!stats[name] || salesTargets[name]?.region === f.branch)
    .map((name) => stats[name] || { name, signed: 0, collected: 0, count: 0, monthSigned: 0, monthCollected: 0, rate: 0 })
    .sort((a, b) => b.signed - a.signed || b.collected - a.collected || a.name.localeCompare(b.name))
    .map((r, i) => ({ ...r, rank: i + 1 }));
  const q = f.q.trim().toLowerCase();
  const rows = allRows
    .filter((r) => !q || r.name.toLowerCase().includes(q))
    .filter((r) => {
      if (f.status === "all") return true;
      const tg = salesTargets[r.name] || {};
      if (f.status === "notarget") return !hasTarget(tg);
      if (f.status === "behind") return isBehind(r, tg);
      return hasTarget(tg) && !isBehind(r, tg);
    })
    .sort((a, b) => (b[f.sort] || 0) - (a[f.sort] || 0));

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/sales-targets/check");
      const names = (data.behind || []).map((b) => b.salesName).join("、");
      alert(lang === "zh"
        ? `已检查 ${data.checked} 名销售，${data.behind.length} 名未达月度目标${names ? "：" + names : ""}，已生成提醒。`
        : `Checked ${data.checked}; ${data.behind.length} behind target${names ? ": " + names : ""}. Reminders created.`);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setSending(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="sales-leaderboard">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
        <Trophy className="h-4 w-4 text-brand-500" />
        <h3 className="font-semibold text-slate-900">{lang === "zh" ? "销售业绩排行 & 目标达成" : "Sales Leaderboard & Targets"}</h3>
        <span className="text-xs text-slate-400">{lang === "zh" ? "签单 / 回款 · 本月环 + 年度环" : "signed / collected · monthly + annual"}</span>
        {canEdit && (
          <button data-testid="send-target-reminders-btn" onClick={sendReminders} disabled={sending} className="ml-auto inline-flex items-center gap-1 text-xs text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-lg px-2.5 py-1.5 transition-colors">
            <BellRing className="h-3.5 w-3.5" />{sending ? (lang === "zh" ? "发送中…" : "Sending…") : (lang === "zh" ? "发送月度目标提醒" : "Send Monthly Reminders")}
          </button>
        )}
      </div>
      <LeaderboardFilters f={f} setF={setF} zh={lang === "zh"} shown={rows.length} total={allRows.length} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium px-5 py-2.5 w-12">#</th>
              <th className="text-left font-medium px-4 py-2.5">{lang === "zh" ? "销售" : "Salesperson"}</th>
              <th className="text-right font-medium px-4 py-2.5">{lang === "zh" ? "签单金额" : "Signed"}</th>
              <th className="text-right font-medium px-4 py-2.5">{lang === "zh" ? "已回款" : "Collected"}</th>
              <th className="text-center font-medium px-4 py-2.5">{lang === "zh" ? "目标达成（本月 / 年度）" : "Target Progress (Month / Annual)"}</th>
              {canEdit && <th className="px-4 py-2.5"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={canEdit ? 6 : 5} className="text-center text-slate-300 py-6">—</td></tr>}
            {rows.map((r, i) => {
              const tg = salesTargets[r.name] || {};
              return (
                <tr key={r.name} className="border-t border-slate-100 hover:bg-slate-50/60 align-middle" data-testid={`leaderboard-row-${i}`}>
                  <td className="px-5 py-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: RANK_TINT[r.rank - 1] || "#94a3b8" }} data-testid={`leaderboard-rank-${i}`}>{r.rank}</span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    <div className="flex items-center gap-2.5">
                      <Avatar src={avatarFor(r.name)} name={r.name} size={34} />
                      <div>
                        {r.name}
                        <p className="text-[11px] text-slate-400 font-normal">{r.count} {lang === "zh" ? "个项目" : "projects"} · {r.rate}% {lang === "zh" ? "回款率" : "rate"}</p>
                        {isBehind(r, tg) && <span data-testid={`behind-badge-${i}`} className="inline-flex items-center gap-0.5 mt-1 text-[10px] font-medium text-red-600 bg-red-50 rounded px-1.5 py-0.5"><AlertTriangle className="h-3 w-3" />{lang === "zh" ? "未达月度目标" : "Below monthly target"}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtMoneyShort(r.signed)}</td>
                  <td className="px-4 py-3 text-right text-green-600">{fmtMoney(r.collected)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-3">
                      <ProgressRing actual={r.monthSigned} target={tg.monthSign || 0} color="#4F6BFF" size={52} label={lang === "zh" ? "月签单" : "M-Sign"} />
                      <ProgressRing actual={r.monthCollected} target={tg.monthCollect || 0} color="#2563eb" size={52} label={lang === "zh" ? "月回款" : "M-Coll"} />
                      <ProgressRing actual={r.signed} target={tg.yearSign || 0} color="#d97706" size={52} label={lang === "zh" ? "年签单" : "Y-Sign"} />
                      <ProgressRing actual={r.collected} target={tg.yearCollect || 0} color="#16a34a" size={52} label={lang === "zh" ? "年回款" : "Y-Coll"} />
                    </div>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`set-target-btn-${i}`} onClick={() => setEditing(r.name)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-500 border border-slate-200 hover:border-brand-300 rounded-lg px-2 py-1 transition-colors">
                        <Target className="h-3.5 w-3.5" />{lang === "zh" ? "设目标" : "Target"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && <TargetDialog salesName={editing} open onClose={() => setEditing(null)} />}
    </div>
  );
}
